import styled, { css } from 'styled-components'
import { media } from '../../../utils/mediaQuery'

export interface MapStyledProps {
  $width?: string | number
  $height?: string | number
}

export const Container = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !prop.startsWith('$') && !['setclickfeature', 'setdimension', 'loaded'].includes(prop),
})<MapStyledProps>`
  /* The app shifts main content left when the Help Center is open. Detail panels remain fixed to the
     viewport, so only the part of the detail panel wider than the Help Center still overlaps the map.
     Use that visible overlap for map chrome offsets, otherwise controls jump too far left. */
  --popety-map-side-offset: max(
    calc(var(--popety-detail-panel-width, 0px) - var(--popety-help-panel-width, 0px)),
    0px
  );

  width: ${({ $width }) => (typeof $width === 'number' ? `${$width}px` : $width || '100%')};
  height: ${({ $height }) => (typeof $height === 'number' ? `${$height}px` : $height || '480px')};
  background: ${({ theme }) => theme.colors.background.tertiary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  position: relative;
  overflow: hidden;

  .mapboxgl-map {
    width: 100%;
    height: 100%;
  }

  /* Style popups */
  .mapboxgl-popup-content {
    padding: 0 !important;
  }

  /* Hide default Mapbox controls */
  .mapboxgl-ctrl-top-left,
  .mapboxgl-ctrl-top-right,
  .mapboxgl-ctrl-bottom-left,
  .mapboxgl-ctrl-bottom-right {
    display: none !important;
  }

  .mapboxgl-ctrl.mapboxgl-ctrl-group {
    display: none !important;
  }

  .mapboxgl-ctrl-logo {
    display: none !important;
  }

  .mapboxgl-ctrl-attrib {
    display: none !important;
  }
`

export const MapSearchContainer = styled.div`
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  width: 100%;
  max-width: 320px;
  padding: 0 12px;

  ${media.tabletDown} {
    top: 12px;
    max-width: calc(100% - 24px);
    padding: 0 12px;
  }

  ${media.mobileOnly} {
    top: 8px;
    max-width: calc(100% - 16px);
    padding: 0 8px;
  }
`

export const ResizeHandle = styled.div`
  position: absolute;
  left: -4px;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: col-resize;
  background: transparent;
  transition: all 0.2s ease;
  z-index: 1001;

  /* Visual indicator bar */
  &::before {
    content: '';
    position: absolute;
    left: 3px;
    top: 50%;
    transform: translateY(-50%);
    width: 2px;
    height: 32px;
    background: ${({ theme }) => theme.colors.border.default};
    border-radius: 2px;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    opacity: 0.15;

    &::before {
      opacity: 0.6;
      background: ${({ theme }) => theme.colors.primary};
    }
  }

  &:active {
    background: ${({ theme }) => theme.colors.primary};
    opacity: 0.25;

    &::before {
      opacity: 1;
      background: ${({ theme }) => theme.colors.primary};
    }
  }

  ${media.tabletDown} {
    display: none;
  }
`

export const MapControls = styled.div`
  position: absolute;
  top: calc(12px + var(--explore-layer-picker-offset, 0px));
  /* Stay clear of the visible right-side overlay. */
  right: calc(12px + var(--popety-map-side-offset, 0px));
  display: flex;
  flex-direction: column;
  gap: 6px;
  z-index: 5;

  ${media.tabletDown} {
    top: calc(56px + var(--explore-layer-picker-offset, 0px));
    right: 8px;
    gap: 4px;
  }

  ${media.mobileOnly} {
    top: calc(56px + var(--explore-layer-picker-offset, 0px));
    right: 8px;
    gap: 3px;
  }
`

export const ControlGroup = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $horizontal?: boolean }>`
  /* background: ${({ theme }) => theme.colors.background.primary}; */
  /* border-radius: 12px; */
  /* box-shadow: ${({ theme }) => theme.shadows.md}; */
  display: flex;
  flex-direction: ${({ $horizontal }) => ($horizontal ? 'row' : 'column')};

  .map-control {
    border-radius: 0;
    box-shadow: none;
    border-bottom: ${({ $horizontal }) => ($horizontal ? 'none' : '1px solid rgba(0,0,0,0.06)')};
    border-right: ${({ $horizontal }) => ($horizontal ? '1px solid rgba(0,0,0,0.06)' : 'none')};

    &:last-child {
      border: none;
    }

    &:first-child {
      border-top-left-radius: 10px;
      border-top-right-radius: ${({ $horizontal }) => ($horizontal ? '0' : '10px')};
      border-bottom-left-radius: ${({ $horizontal }) => ($horizontal ? '10px' : '0')};
    }

    &:last-child {
      border-top-right-radius: ${({ $horizontal }) => ($horizontal ? '10px' : '0')};
      border-bottom-left-radius: ${({ $horizontal }) => ($horizontal ? '0' : '10px')};
      border-bottom-right-radius: 10px;
    }
  }
`

export const MapFAB = styled.button.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $active?: boolean }>`
  position: absolute;
  bottom: 20px;
  /* Stay clear of the visible right-side overlay — see MapControls. */
  right: calc(12px + var(--popety-map-side-offset, 0px));
  width: 32px;
  height: 32px;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  border: none;
  border-radius: 20px;
  box-shadow: 0 2px 8px rgba(2, 148, 223, 0.3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    background 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2;
  transform: ${({ $active }) => ($active ? 'rotate(45deg)' : 'rotate(0deg)')};

  &:hover {
    box-shadow: 0 4px 12px rgba(2, 148, 223, 0.4);
  }

  &:active {
    transform: scale(0.95) ${({ $active }) => ($active ? 'rotate(45deg)' : 'rotate(0deg)')};
  }

  .icon {
    width: 20px;
    height: 20px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
  }

  ${media.tabletDown} {
    bottom: 24px;
    right: 8px;
  }

  ${media.mobileOnly} {
    bottom: 16px;
    right: 8px;
    width: 36px;
    height: 36px;
  }

  /* Touch-friendly size on touch devices */
  ${media.touch} {
    width: 44px;
    height: 44px;

    &:hover {
      box-shadow: 0 2px 8px rgba(2, 148, 223, 0.3);
    }

    &:active {
      box-shadow: 0 4px 12px rgba(2, 148, 223, 0.4);
      transform: scale(0.95) ${({ $active }) => ($active ? 'rotate(45deg)' : 'rotate(0deg)')};
    }
  }

  ${media.mobileOnly} {
    width: 36px;
    height: 36px;
  }
`

export const DrawTools = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $active?: boolean }>`
  position: absolute;
  bottom: 112px;
  /* Stay clear of the visible right-side overlay — see MapControls. */
  right: calc(12px + var(--popety-map-side-offset, 0px));
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: 6px;
  display: ${({ $active }) => ($active ? 'flex' : 'none')};
  flex-direction: column;
  gap: 3px;
  min-width: 144px;
  transform-origin: bottom right;
  animation: ${({ $active }) => ($active ? 'popIn 0.2s cubic-bezier(0.4, 0, 0.2, 1)' : 'none')};
  z-index: 2;

  @keyframes popIn {
    from {
      opacity: 0;
      transform: scale(0.8) translateY(10px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  ${media.tabletDown} {
    bottom: 140px;
    right: 8px;
    min-width: 160px;
    max-width: calc(100vw - 32px);
  }

  ${media.mobileOnly} {
    bottom: 60px;
    right: 8px;
    min-width: 140px;
    max-width: calc(100vw - 24px);
    padding: 6px;
  }
`

export const DrawToolButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $active?: boolean; $variant?: 'default' | 'delete' }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  color: ${({ $variant, theme }) =>
    $variant === 'delete' ? theme.colors.danger : theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};

  &:hover {
    background: ${({ $variant, theme }) =>
      $variant === 'delete' ? 'rgba(255,59,48,0.1)' : theme.colors.background.tertiary};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.primaryLight};
      color: ${theme.colors.primary};
    `}

  .icon {
    width: 12px;
    height: 12px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
    flex-shrink: 0;
  }
`

export const DrawToolsDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border.light};
  margin: 3px 6px;
`

export const LayersPanel = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $active?: boolean }>`
  position: absolute;
  top: 0;
  right: -224px;
  width: 224px;
  height: 100%;
  background: ${({ theme }) => theme.colors.background.primary};
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 10;
  display: flex;
  flex-direction: column;
  border-radius: 6px 0 0 6px;
  transform: ${({ $active }) => ($active ? 'translateX(-224px)' : 'translateX(0)')};

  ${media.tabletDown} {
    width: calc(100vw - 32px);
    max-width: 320px;
    right: calc((100vw - 32px) * -1);
    transform: ${({ $active }) =>
      $active ? `translateX(calc((100vw - 32px) * -1 + 16px))` : 'translateX(0)'};
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
`

export const LayersHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const LayersTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.title};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`

export const LayersClose = styled.button`
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  .icon {
    width: 12px;
    height: 12px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
  }

  /* Mobile: Ensure 44px minimum touch target */
  ${media.tabletDown} {
    width: ${({ theme }) => theme.touchTargets.minimum};
    height: ${({ theme }) => theme.touchTargets.minimum};
  }

  /* Disable hover effects on touch devices */
  @media (hover: none) {
    &:hover {
      background: transparent;
      color: ${({ theme }) => theme.colors.text.secondary};
    }

    &:active {
      background: ${({ theme }) => theme.colors.background.tertiary};
      color: ${({ theme }) => theme.colors.text.primary};
    }
  }
`

export const LayersContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
`

export const LayerGroup = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`

export const LayerGroupTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-bottom: 10px;
  padding: 0 3px;
`

export const LayerItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
  }
`

export const LayerLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
`

export const LegendPanel = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $active?: boolean }>`
  position: absolute;
  bottom: 20px;
  right: 12px;
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: 10px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: 12px;
  min-width: 160px;
  max-width: 224px;
  display: ${({ $active }) => ($active ? 'block' : 'none')};
  z-index: 5;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${({ $active }) => ($active ? 'fadeInUp 0.3s ease' : 'none')};

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  ${media.tabletDown} {
    bottom: 24px;
    right: 8px;
    max-width: 240px;
  }

  ${media.mobileOnly} {
    bottom: 16px;
    right: 8px;
    max-width: 200px;
    padding: 12px;
  }
`

export const LegendHeader = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
`

export const LegendContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const LegendGradient = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $gradient?: string }>`
  width: 100%;
  height: 20px;
  border-radius: 3px;
  margin-bottom: 6px;
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: ${({ $gradient }) => $gradient || 'linear-gradient(to right, #e0e0e0, #bdbdbd)'};
`

export const LegendLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-bottom: 3px;
`

export const LegendUnit = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  color: ${({ theme }) => theme.colors.text.tertiary};
  text-align: center;
  margin-top: 3px;
`

export const HoverBox = styled.div<{ left?: number; top?: number }>`
  position: absolute;
  left: ${({ left }) => (left ? `${left}px` : '0')};
  top: ${({ top }) => (top ? `${top}px` : '0')};
  transform: translate(-50%, -100%);
  margin-top: -10px;
  pointer-events: none;
  z-index: 100;
  background: white;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 10px;
  min-width: 160px;
  font-size: 12px;
`

export const FullscreenButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $isFullscreen?: boolean }>`
  position: absolute;
  top: 12px;
  left: 12px;
  width: 32px;
  height: 32px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: none;
  border-radius: 6px;
  box-shadow: ${({ theme }) => theme.shadows.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1002;

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
    box-shadow: ${({ theme }) => theme.shadows.lg};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  .icon {
    width: 15px;
    height: 15px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
  }

  ${media.tabletDown} {
    top: 12px;
    left: 12px;
  }

  ${media.touch} {
    width: 44px;
    height: 44px;

    .icon {
      width: 16px;
      height: 16px;
    }
  }

  /* Disable hover effects on touch devices */
  @media (hover: none) {
    &:hover {
      background: ${({ theme }) => theme.colors.background.primary};
      box-shadow: ${({ theme }) => theme.shadows.md};
      transform: none;
    }

    &:active {
      background: ${({ theme }) => theme.colors.background.tertiary};
      transform: scale(0.95);
    }
  }
`
